import Input from "components/Input";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useState } from "react";
import Button from "components/Button";
import settingsService, {
  IConfigureIntegrationRequest,
  MailServerType,
} from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import Checkbox from "components/Checkbox";
import HollowButton from "components/HollowButton";
import { useAuth } from "react-oidc-context";
import { IDropdownObject } from "components/Dropdown/typings";
import DropdownMui from "components/DropdownMui";
import { ISingleBinaryKeys } from "utils/GenerateSingleBinary";
import {
  CompanyKeyMissingError,
  encryptForCompany,
} from "utils/encryptForCompany";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const getPort = (type: MailServerType, security: number) => {
  if (type === MailServerType.Imap) {
    switch (security) {
      case 0:
        return "143";
      case 1:
        return "993";
    }
  } else {
    switch (security) {
      case 0:
        return "110";
      case 1:
        return "995";
    }
  }
  return "";
};

const ConfigureIMAP: FC<Props> = ({
  onSuccess,
  toggle,
  initialEmail,
  initialLabel,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();

  const historyRange: IDropdownObject[] = [
    { value: 30, name: t("integrations.days30") },
    { value: 60, name: t("integrations.days60") },
    { value: 180, name: t("integrations.days180") },
    { value: 1, name: t("integrations.year1") },
    { value: 2, name: t("integrations.years2") },
    { value: 3, name: t("integrations.years3") },
    { value: 0, name: t("integrations.fullInbox") },
  ];

  const { mutate: integration, isLoading } =
    settingsService.useConfigureIntegration();

  const [data, setData] = useState({
    email: initialEmail ?? "",
    password: "",
    label: initialLabel ?? auth.user?.profile.name ?? "",
    incomingServer: {
      name: "",
      port: "143",
      security: 0,
    },
    outcomingServer: {
      name: "",
      port: "587",
      security: 0,
    },
    historyRange: 30,
  });

  const [needMoreData, setNeedMoreData] = useState(false);
  const [error, setError] = useState<string>(null);

  const [incomingServerType, setIncomingServerType] = useState<MailServerType>(
    MailServerType.Imap,
  );

  const handleIncomingServerTypeChanged = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIncomingServerType(Number((event.target as HTMLInputElement).value));

    setData({
      ...data,
      incomingServer: {
        ...data.incomingServer,
        port: getPort(
          Number((event.target as HTMLInputElement).value),
          data.incomingServer.security,
        ),
      },
    });
  };

  const handleClick = async () => {
    // Klucz integracji owija klucz publiczny FIRMY (docs/model-kluczy-firmowych.md,
    // inwariant I1), a nie klucz profilu ani klucz intermediate użytkownika — skrzynka
    // podpięta przez jedną osobę ma być czytelna dla całej kancelarii z grantem.
    let key: ISingleBinaryKeys;
    try {
      key = await encryptForCompany();
    } catch (error) {
      // FAIL-CLOSED — patrz utils/encryptForCompany.ts.
      notification({
        type: ENotificationType.ERROR,
        title: t("integrations.createIntegrationFailed"),
        text:
          error instanceof CompanyKeyMissingError
            ? error.message
            : t("errors.somethingWentWrong"),
      });
      return;
    }

    let payload: IConfigureIntegrationRequest = {
      Type: 0,
      Provider: 5,
      Email: data.email,
      Label: data.label,
      ImapLogin: data.email,
      ImapPassword: data.password,
      PublicKey: key.PublicKey,
      PrivateKey: key.PrivateKey,
      HistoryRange: data.historyRange,
    };

    if (needMoreData) {
      payload = {
        ...payload,
        ServerConfiguration: {
          IncomingHost: data.incomingServer.name,
          IncomingPort: Number(data.incomingServer.port),
          IncomingSecurity: data.incomingServer.security,
          OutgoingHost: data.outcomingServer.name,
          OutgoingPort: Number(data.outcomingServer.port),
          OutgoingSecurity: data.outcomingServer.security,
          IncomingType: incomingServerType,
        },
      };
    }

    setError(null);
    integration(payload, {
      onSuccess: () => {
        onSuccess(data.email);
        toggle();
      },
      onError: (response: { response: { data: { errorCodes: { code: string; message: string }[] } } }) => {
        const code = response.response.data.errorCodes[0].code;
        const name = response.response.data.errorCodes[0].message;
        if (code === "ProvideServerConfiguration") {
          notification({
            type: ENotificationType.ERROR,
            title: t("integrations.createIntegrationFailed"),
            text: t("integrations.serverReadFailed"),
          });
          setNeedMoreData(true);
          return;
        }
        notification({
          type: ENotificationType.ERROR,
          title: t("integrations.createIntegrationFailed"),
          text: name,
        });
        setError(name);
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.form}>
        <Input
          value={data.email}
          onChange={(text) => setData({ ...data, email: text })}
          label={t("auth.email")}
        />
        <Input
          value={data.password}
          type="password"
          onChange={(text) => setData({ ...data, password: text })}
          label={t("auth.password")}
        />
        <Input
          value={data.label}
          onChange={(text) => setData({ ...data, label: text })}
          label="Dodaj etykietę skrzynki"
        />

        <div className={styles.title}>{t("integrations.syncRangeTitle")}</div>
        <div className={styles.body}>{t("integrations.syncRangeBody")}</div>

        <DropdownMui
          className={styles.historyDropdown}
          onChange={(r) =>
            setData({ ...data, historyRange: r.value as number })
          }
          list={historyRange}
          label={t("integrations.syncRangeTitle")}
          defaultValue={30}
        />

        {!needMoreData && (
          <a
            onClick={() => {
              setNeedMoreData(true);
            }}
          >
            {t("integrations.advancedSettings")}
          </a>
        )}
        {needMoreData && (
          <>
            <div className={styles.title}>
              {t("integrations.advancedSettings")}
            </div>
            <div className={styles.body}>
              {t("integrations.advancedSettingsBody")}
            </div>
            <div className={styles.connection}>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={incomingServerType}
                  onChange={handleIncomingServerTypeChanged}
                >
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label="IMAP"
                  />
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label="POP3"
                  />
                </RadioGroup>
              </FormControl>
              <div className={styles.group2}>
                <Input
                  label={
                    incomingServerType === MailServerType.Imap
                      ? t("settings.imapServer")
                      : t("settings.pop3Server")
                  }
                  onChange={(text) => {
                    setData({
                      ...data,
                      incomingServer: { ...data.incomingServer, name: text },
                    });
                  }}
                  value={data.incomingServer.name}
                />
                <Input
                  label={t("integrations.port")}
                  onChange={(text) => {
                    setData({
                      ...data,
                      incomingServer: { ...data.incomingServer, port: text },
                    });
                  }}
                  value={data.incomingServer.port}
                />
                <div className={styles.checkbox}>
                  <Checkbox
                    value={!!data.incomingServer.security}
                    onChange={() => {
                      const newSecurity =
                        data.incomingServer.security === 1 ? 0 : 1;
                      setData({
                        ...data,
                        incomingServer: {
                          ...data.incomingServer,
                          port: getPort(incomingServerType, newSecurity),
                          security: newSecurity,
                        },
                      });
                    }}
                  />
                  {t("integrations.encrypted")}
                </div>
              </div>
            </div>

            <div className={styles.connection}>
              <div className={styles.group2}>
                <Input
                  label={t("settings.smtpServer")}
                  onChange={(text) => {
                    setData({
                      ...data,
                      outcomingServer: { ...data.outcomingServer, name: text },
                    });
                  }}
                  value={data.outcomingServer.name}
                />
                <Input
                  label={t("integrations.port")}
                  onChange={(text) => {
                    setData({
                      ...data,
                      outcomingServer: { ...data.outcomingServer, port: text },
                    });
                  }}
                  value={data.outcomingServer.port}
                />
                <div className={styles.checkbox}>
                  <Checkbox
                    value={!!data.outcomingServer.security}
                    onChange={() => {
                      const newSecurity =
                        data.outcomingServer.security === 1 ? 0 : 1;

                      const port = !newSecurity ? "587" : "465";
                      setData({
                        ...data,
                        outcomingServer: {
                          ...data.outcomingServer,

                          port: port,
                          security: newSecurity,
                        },
                      });
                    }}
                  />
                  {t("integrations.encrypted")}
                </div>
              </div>
            </div>
          </>
        )}
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.buttonsWrapper}>
          <HollowButton text={t("common.cancel")} onClick={() => toggle()} />
          <Button
            text={t("common.save")}
            onClick={handleClick}
            loading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfigureIMAP;
